import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function GameSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    gameApi.get(`/game/auth/setup-info?token=${encodeURIComponent(token)}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/auth/complete-setup", { token, password: pw });
      localStorage.setItem("bmm_game_token", data.token);
      toast.success("Account ready! Welcome, " + data.player.display_name);
      navigate("/game", { replace: true });
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not complete setup.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-10" data-testid="game-setup-page">
      <div className="mx-auto max-w-sm rounded-2xl border border-border bg-card p-6 shadow-xl">
        <div className="mb-3 flex items-center gap-2 text-primary">
          <ShieldCheck size={22} />
          <h1 className="font-display text-2xl font-bold">Account Setup</h1>
        </div>
        {state === "loading" && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Checking your setup link…</p>
        )}
        {state === "invalid" && (
          <div data-testid="setup-invalid">
            <p className="text-sm text-destructive font-semibold">This setup link is invalid or has expired.</p>
            <p className="mt-2 text-sm text-muted-foreground">Please request a new setup link.</p>
          </div>
        )}
        {state === "ready" && (
          <form onSubmit={submit} className="space-y-3" data-testid="setup-form">
            <p className="text-sm text-muted-foreground">
              Welcome, <span className="font-bold text-primary">{info.display_name}</span>
              {info.player_number ? <> — Player #{info.player_number}</> : null}
              {info.role === "game_admin" ? <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">Game Admin</span> : null}
            </p>
            <p className="text-xs text-muted-foreground">{info.email}</p>
            <input data-testid="setup-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Create a password (min 8 characters)" autoComplete="new-password"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
            <input data-testid="setup-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              placeholder="Confirm password" autoComplete="new-password"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
            <button data-testid="setup-submit" type="submit" disabled={busy}
              className="h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">
              {busy ? "Setting up…" : "Create Password & Log In"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
